"use client"

import * as React from "react"
import { usePathname } from "next/navigation"

export type MenuItem = {
    label: string
    href: string
    icon?: React.ComponentType<{ className?: string }>
}

export type ActiveStyle =
    | "soft-glow"
    | "glass"
    | "edge"
    | "ink"
    | "float"

export type MenuProps = {
    items: MenuItem[]
    LinkComponent: React.ComponentType<any>
    activeStyle?: ActiveStyle
    size?: "sm" | "md"
    className?: string
}

const SIZE = {
    sm: "px-2 py-1 text-sm",
    md: "px-3 py-2",
}

const BASE =
    "inline-flex items-center gap-2 rounded-md text-primary-foreground transition-all duration-150 ease-out"

const ACTIVE: Record<ActiveStyle, string> = {
    "soft-glow": "bg-white/12 shadow-[0_0_0_1px_rgba(255,255,255,0.25)]",
    glass: "bg-white/20 backdrop-blur border border-white/20",
    edge: "border-l-2 border-white/70 pl-2",
    ink: "font-semibold underline underline-offset-4 decoration-2",
    float: "bg-white/14 shadow-md -translate-y-0.5",
}

const HOVER: Record<ActiveStyle, string> = {
    "soft-glow": "hover:bg-white/10",
    glass: "hover:bg-white/25 hover:border-white/30",
    edge: "hover:border-l-2 hover:border-white hover:bg-white/10",
    ink: "hover:underline hover:decoration-2",
    float: "hover:bg-white/12 hover:-translate-y-0.5 hover:shadow-lg",
}

export function PlainMenu({
    items,
    LinkComponent,
    activeStyle = "soft-glow",
    size = "md",
    className,
}: MenuProps) {

    const pathname = usePathname()
    const [mounted, setMounted] = React.useState(false)
    const [activeHash, setActiveHash] = React.useState<string | null>(null)

    React.useEffect(() => {
        setMounted(true)
    }, [])

    // Scroll spy
    React.useEffect(() => {
        if (!mounted) return

        const anchors = items
            .filter(i => i.href.startsWith("#"))
            .map(i => i.href)

        if (anchors.length === 0) return

        const sections = anchors
            .map(h => {
                const el = document.querySelector(h)
                if (!el) return null
                return { hash: h, el }
            })
            .filter(Boolean) as { hash: string; el: Element }[]

        const observer = new IntersectionObserver(
            entries => {
                const visible = entries
                    .filter(e => e.isIntersecting)
                    .sort((a, b) => b.intersectionRatio - a.intersectionRatio)

                if (visible.length > 0) {
                    const hash = "#" + visible[0].target.id
                    setActiveHash(hash)
                }
            },
            { threshold: [0.25, 0.5, 0.75] }
        )

        sections.forEach(s => observer.observe(s.el))

        return () => observer.disconnect()
    }, [mounted, items])

    if (!mounted || !pathname) return null

    return (
        <nav className={["flex items-center gap-1", className ?? ""].join(" ")}>
            {items.map(item => {
                const Icon = item.icon
                const isAnchor = item.href.startsWith("#")

                let active = false

                if (isAnchor) {
                    active = activeHash === item.href
                } else {
                    active =
                        item.href === "/"
                            ? pathname === "/"
                            : pathname === item.href
                }

                const handleClick = (e: React.MouseEvent) => {
                    if (!isAnchor) return

                    e.preventDefault()
                    const target = document.querySelector(item.href)
                    if (target) target.scrollIntoView({ behavior: "smooth" })

                    history.replaceState(null, "", item.href)
                }

                return (
                    <LinkComponent
                        key={item.href}
                        href={item.href}
                        onClick={handleClick}
                        className={[
                            BASE,
                            SIZE[size],
                            active ? ACTIVE[activeStyle] : "",
                            HOVER[activeStyle],
                        ].join(" ")}
                    >
                        {Icon && <Icon className="h-4 w-4" />}
                        <span>{item.label}</span>
                    </LinkComponent>
                )
            })}
        </nav>
    )
}
